/**
 * Mobile Clock-In Page (DRR-221 Phase 2a)
 *
 * Minimal mobile-optimized page for EVV-compliant clock-in.
 * Accessed via SMS deep link: /clock-in/{scheduleId}
 *
 * Flow:
 *   1. User taps link in SMS
 *   2. Page loads, requests GPS permission
 *   3. User confirms clock-in
 *   4. GPS captured + clock-in recorded
 *   5. Success message shown
 *
 * Design principles:
 *   - Works on any device (responsive, no app install)
 *   - Single action (one tap to clock in)
 *   - Clear feedback (success/error states)
 *   - Offline-aware (shows error if no connection)
 *   - NO AUTH REQUIRED - public page accessed via SMS link
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Alert,
  Paper,
  Group,
  Loader,
  ThemeIcon,
  Badge,
  MantineProvider,
} from '@mantine/core';
import {
  IconMapPin,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconUser,
} from '@tabler/icons-react';

interface GpsData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: string;
}

type PageState = 'loading' | 'ready' | 'capturing' | 'submitting' | 'success' | 'error';

// Inline GPS helper (no external dependencies)
function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  });
}

export function MobileClockInPage() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const [searchParams] = useSearchParams();

  // Schedule info from query params (passed from SMS link)
  const clientName = searchParams.get('client') || 'Client';
  const scheduledTime = searchParams.get('time') || '';
  const employeeId = searchParams.get('emp') || '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [gpsData, setGpsData] = useState<GpsData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Check GPS availability on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setPageState('error');
      setErrorMessage('GPS not available on this device');
      return;
    }

    // Request GPS permission proactively
    navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'denied') {
        setPageState('error');
        setErrorMessage('GPS permission denied. Please enable location access.');
      } else {
        setPageState('ready');
      }
    }).catch(() => {
      // Permissions API not available, try GPS directly
      setPageState('ready');
    });
  }, []);

  const captureGps = useCallback(async () => {
    setPageState('capturing');
    setErrorMessage('');

    try {
      const position = await getCurrentPosition();
      setGpsData({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString(),
      });
      setPageState('ready');
    } catch (error: unknown) {
      setPageState('error');
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage('GPS permission denied. Please enable location access in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorMessage('GPS signal unavailable. Please try again outside or near a window.');
            break;
          case error.TIMEOUT:
            setErrorMessage('GPS timed out. Please try again.');
            break;
          default:
            setErrorMessage('Could not get GPS location. Please try again.');
        }
      } else {
        setErrorMessage('Could not get GPS location. Please try again.');
      }
    }
  }, []);

  const handleClockIn = useCallback(async () => {
    if (!gpsData) {
      // Capture GPS first
      await captureGps();
      return;
    }

    setPageState('submitting');
    setErrorMessage('');

    try {
      // Submit clock-in via public API endpoint
      const response = await fetch('/api/public/clock-in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scheduleId,
          employeeId,
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
          accuracy: gpsData.accuracy,
          timestamp: gpsData.timestamp,
          source: 'MOBILE_SMS_LINK',
          deviceInfo: navigator.userAgent,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Clock-in failed');
      }

      setPageState('success');
    } catch (error: unknown) {
      setPageState('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not complete clock-in. Please try again.'
      );
    }
  }, [gpsData, employeeId, scheduleId, captureGps]);

  // Auto-capture GPS on ready
  useEffect(() => {
    if (pageState === 'ready' && !gpsData) {
      captureGps();
    }
  }, [pageState, gpsData, captureGps]);

  return (
    <MantineProvider>
      <Container size="xs" py="xl" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        <Stack gap="lg">
          {/* Header */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="xs">
              <Group justify="space-between">
                <Title order={3}>FERP Clock-In</Title>
                <Badge color="blue" variant="light">
                  EVV
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Electronic Visit Verification
              </Text>
            </Stack>
          </Paper>

          {/* Schedule Info */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="sm">
              <Group gap="xs">
                <ThemeIcon size="sm" variant="light" color="gray">
                  <IconUser size={14} />
                </ThemeIcon>
                <Text fw={500}>{clientName}</Text>
              </Group>
              {scheduledTime && (
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="gray">
                    <IconClock size={14} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed">
                    Scheduled: {scheduledTime}
                  </Text>
                </Group>
              )}
            </Stack>
          </Paper>

          {/* GPS Status */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="sm">
              <Group gap="xs">
                <ThemeIcon
                  size="sm"
                  variant="light"
                  color={gpsData ? 'green' : pageState === 'capturing' ? 'blue' : 'gray'}
                >
                  <IconMapPin size={14} />
                </ThemeIcon>
                <Text size="sm" fw={500}>
                  GPS Location
                </Text>
              </Group>

              {pageState === 'capturing' && (
                <Group gap="xs">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">
                    Getting your location...
                  </Text>
                </Group>
              )}

              {gpsData && (
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">
                    Lat: {gpsData.latitude.toFixed(6)}, Lng: {gpsData.longitude.toFixed(6)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Accuracy: {Math.round(gpsData.accuracy)}m
                  </Text>
                </Stack>
              )}

              {!gpsData && pageState !== 'capturing' && pageState !== 'loading' && (
                <Text size="sm" c="dimmed">
                  Tap &quot;Clock In&quot; to capture GPS
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Error Alert */}
          {pageState === 'error' && errorMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              variant="light"
            >
              {errorMessage}
            </Alert>
          )}

          {/* Success State */}
          {pageState === 'success' && (
            <Alert
              icon={<IconCheck size={16} />}
              title="Clock-In Successful"
              color="green"
              variant="light"
            >
              <Stack gap="xs">
                <Text size="sm">
                  You have been clocked in for {clientName}.
                </Text>
                <Text size="xs" c="dimmed">
                  GPS location recorded for EVV compliance.
                </Text>
              </Stack>
            </Alert>
          )}

          {/* Action Button */}
          {pageState !== 'success' && (
            <Button
              size="xl"
              fullWidth
              color={gpsData ? 'green' : 'blue'}
              loading={pageState === 'submitting' || pageState === 'capturing'}
              disabled={pageState === 'loading'}
              leftSection={
                pageState === 'submitting' ? null : gpsData ? (
                  <IconCheck size={20} />
                ) : (
                  <IconMapPin size={20} />
                )
              }
              onClick={handleClockIn}
            >
              {pageState === 'submitting'
                ? 'Clocking In...'
                : pageState === 'capturing'
                  ? 'Getting GPS...'
                  : gpsData
                    ? 'Confirm Clock-In'
                    : 'Clock In'}
            </Button>
          )}

          {/* Retry button for errors */}
          {pageState === 'error' && (
            <Button
              variant="light"
              fullWidth
              onClick={() => {
                setPageState('ready');
                setErrorMessage('');
                setGpsData(null);
              }}
            >
              Try Again
            </Button>
          )}

          {/* Close button for success */}
          {pageState === 'success' && (
            <Button
              variant="light"
              fullWidth
              onClick={() => window.close()}
            >
              Close
            </Button>
          )}

          {/* Footer */}
          <Text size="xs" c="dimmed" ta="center">
            FERP Electronic Visit Verification
            <br />
            GPS data is recorded for Medicaid compliance
          </Text>
        </Stack>
      </Container>
    </MantineProvider>
  );
}

export default MobileClockInPage;
